import React, { useState, useRef } from 'react';
import { ACTIVITY_IMAGES } from '../../data/mockData';
import DateTimePicker from './DateTimePicker';
import HelpTip from './HelpTip';
import { isActivityObfuscated } from '../../services/activityVisibility';
import { defaultIsCompetition, isCompetitionActivity } from '../../services/activityTraits';

const TYPE_OPTIONS = [
  { value: 'teamselection', label: '👥 Team Selection' },
  { value: 'betting', label: '🎯 Betting' },
  { value: 'openbets', label: '🤝 Open Bets' },
  { value: 'quiz', label: '🧠 Quiz' },
  { value: 'vote', label: '🗳️ Vote' },
  { value: 'shuffleboard', label: '🏒 Shuffleboard' },
  { value: 'food', label: '🍽️ Food' },
  { value: 'game', label: '🎮 Game' },
  { value: 'transport', label: '🚀 Transport' },
  { value: 'accommodation', label: '🏨 Accommodation' },
  { value: 'custom', label: '📋 Custom' },
];

const TRANSPORT_MODES = [
  { id: 'train', icon: '🚆', label: 'Train' },
  { id: 'flight', icon: '✈️', label: 'Flight' },
  { id: 'car', icon: '🚗', label: 'Car' },
  { id: 'boat', icon: '⛵', label: 'Boat' },
  { id: 'bus', icon: '🚌', label: 'Bus' },
  { id: 'taxi', icon: '🚕', label: 'Taxi' },
  { id: 'walk', icon: '🚶', label: 'Walk' },
  { id: 'bike', icon: '🚲', label: 'Bike' },
];

const ACCOMMODATION_TYPES = [
  { id: 'hotel', icon: '🏨', label: 'Hotel' },
  { id: 'hostel', icon: '🛏️', label: 'Hostel' },
  { id: 'airbnb', icon: '🏠', label: 'Airbnb' },
  { id: 'camping', icon: '⛺', label: 'Camping' },
  { id: 'resort', icon: '🏖️', label: 'Resort' },
  { id: 'cabin', icon: '🏡', label: 'Cabin' },
];

const s = {
  form: { display: 'flex', flexDirection: 'column', gap: 10 },
  row: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  input: { flex: 1, minWidth: 140, padding: '9px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  select: { padding: '9px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 13, outline: 'none', cursor: 'pointer' },
  textarea: { width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' },
  label: { fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 3, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  section: { marginBottom: 4 },
  imgBtn: (sel) => ({ padding: '6px 8px', borderRadius: 6, border: sel ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.08)', background: sel ? 'rgba(59,130,246,0.1)' : 'rgba(255,255,255,0.03)', cursor: 'pointer', fontSize: 18, transition: 'all 0.12s' }),
  transportBtn: (sel) => ({ flex: 1, padding: '8px 6px', textAlign: 'center', borderRadius: 8, border: sel ? '1px solid rgba(59,130,246,0.4)' : '1px solid rgba(255,255,255,0.08)', background: sel ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)', cursor: 'pointer', color: '#fff', fontSize: 13 }),
  submit: { padding: '10px 20px', background: '#22c55e', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' },
  cancel: { padding: '10px 16px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer' },
  checkbox: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'rgba(255,255,255,0.7)', cursor: 'pointer' },
  option: { display: 'inline-flex', alignItems: 'center' },
};

export default function ActivityForm({ initial, masterEvent, onSubmit, onCancel, submitLabel, compact }) {
  const isEditing = !!initial?.id;
  const defaults = {
    name: '', type: 'custom', description: '', isTeamBased: false,
    date: '', image: '', imageType: '', locationName: '', locationLink: '',
    transportMode: 'car', accommodationType: 'hotel', hint: '',
    publishImmediately: false, ...initial,
  };
  defaults.obfuscated = isActivityObfuscated(initial, masterEvent);
  defaults.isCompetition = isCompetitionActivity(defaults);
  // Extract location fields from nested object
  if (initial?.location) {
    defaults.locationName = initial.location.name || '';
    defaults.locationLink = initial.location.link || '';
  }
  // Coerce nullable string fields to '' so .trim() is always safe
  ['name', 'description', 'date', 'image', 'imageType', 'locationName', 'locationLink', 'hint'].forEach((k) => {
    if (defaults[k] == null) defaults[k] = '';
  });

  const [form, setForm] = useState(defaults);
  const [iconSearch, setIconSearch] = useState('');
  const fileRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm({ ...form, image: ev.target.result, imageType: 'upload' });
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const locationData = (form.locationName.trim() || form.locationLink.trim()) ? { name: form.locationName.trim(), link: form.locationLink.trim() || null } : null;
    const payload = {
      name: form.name.trim(),
      type: form.type,
      description: form.description.trim(),
      isCompetition: form.isCompetition,
      isTeamBased: form.isCompetition && form.isTeamBased,
      date: form.date || null,
      image: form.image || null,
      imageType: form.imageType || null,
      location: locationData,
      transportMode: form.type === 'transport' ? form.transportMode : null,
      accommodationType: form.type === 'accommodation' ? form.accommodationType : null,
      hint: form.hint.trim() || null,
      obfuscated: form.obfuscated,
    };
    if (!isEditing) payload.status = form.publishImmediately ? 'active' : 'draft';
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} style={s.form}>
      <div style={s.row}>
        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Activity name" style={s.input} autoFocus />
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, ...(!isEditing && !form.isTeamBased && { isCompetition: defaultIsCompetition(e.target.value) }) })} style={s.select}>
          {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" rows={2} style={s.textarea} />

      {/* Date */}
      <div style={s.section}>
        <span style={s.label}>Date & Time<HelpTip label="Date and time">Places the activity on the event timeline and orders the Scores page. Hidden from members while the activity is obfuscated.</HelpTip></span>
        <DateTimePicker value={form.date} onChange={(val) => setForm({ ...form, date: val })} placeholder="Pick a date & time..." />
      </div>

      {/* Transport mode */}
      {form.type === 'transport' && (
        <div style={s.section}>
          <span style={s.label}>Transport Mode</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {TRANSPORT_MODES.map((m) => (
              <button key={m.id} type="button" onClick={() => setForm({ ...form, transportMode: m.id })} style={s.transportBtn(form.transportMode === m.id)}>
                <div style={{ fontSize: 18 }}>{m.icon}</div>
                <div style={{ fontSize: 10, marginTop: 2 }}>{m.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Accommodation type */}
      {form.type === 'accommodation' && (
        <div style={s.section}>
          <span style={s.label}>Accommodation Type</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {ACCOMMODATION_TYPES.map((m) => (
              <button key={m.id} type="button" onClick={() => setForm({ ...form, accommodationType: m.id })} style={s.transportBtn(form.accommodationType === m.id)}>
                <div style={{ fontSize: 18 }}>{m.icon}</div>
                <div style={{ fontSize: 10, marginTop: 2 }}>{m.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Image */}
      {!compact && (
        <div style={s.section}>
          <span style={s.label}>Image / Icon</span>
          <div style={{ marginBottom: 6, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="text" value={iconSearch} onChange={(e) => setIconSearch(e.target.value)} placeholder="Search icons..." style={{ ...s.input, flex: 1, minWidth: 100, fontSize: 12, padding: '6px 10px' }} />
            <input type="file" accept="image/*" ref={fileRef} onChange={handleFileUpload} style={{ display: 'none' }} />
            <button type="button" onClick={() => fileRef.current?.click()} style={{ ...s.cancel, padding: '6px 10px', fontSize: 11 }}>Upload</button>
            {form.image && (
              <button type="button" onClick={() => setForm({ ...form, image: '', imageType: '' })} style={{ ...s.cancel, padding: '6px 10px', fontSize: 11, color: '#ef4444' }}>Clear</button>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', maxHeight: 120, overflowY: 'auto' }}>
            {ACTIVITY_IMAGES
              .filter((img) => !iconSearch || img.label.toLowerCase().includes(iconSearch.toLowerCase()))
              .map((img) => (
                <button key={img.id} type="button" onClick={() => { setForm({ ...form, image: img.emoji, imageType: 'default' }); setIconSearch(''); }} style={s.imgBtn(form.image === img.emoji && form.imageType === 'default')} title={img.label}>
                  {img.emoji}
                </button>
              ))}
            {form.image && form.imageType === 'upload' && <img src={form.image} alt="" style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover' }} />}
          </div>
        </div>
      )}

      {/* Location */}
      <div style={s.section}>
        <span style={s.label}>{form.type === 'transport' ? 'Departure / Destination' : form.type === 'accommodation' ? 'Accommodation Address' : 'Meetup Location'}</span>
        <input type="text" value={form.locationName} onChange={(e) => setForm({ ...form, locationName: e.target.value })} placeholder="Location name" style={{ ...s.input, marginBottom: 4, width: '100%' }} />
        <input type="text" value={form.locationLink} onChange={(e) => setForm({ ...form, locationLink: e.target.value })} placeholder="Google Maps link (optional)" style={{ ...s.input, width: '100%' }} />
      </div>

      <div style={s.section}>
        <span style={s.option}>
          <label style={s.checkbox}>
            <input type="checkbox" checked={form.obfuscated} onChange={(e) => setForm({ ...form, obfuscated: e.target.checked })} />
            Obfuscate activity
          </label>
          <HelpTip label="Obfuscate activity">Keeps the activity a surprise. Members see 🙈 and your hint on the timeline and cards; admins always see everything and get a 🙈 badge. Reveal it any time from the activity or Admin.</HelpTip>
        </span>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: '6px 0 10px' }}>
          Hide the name, description, image, date and location from members until revealed. Admins always see the full details.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
          <label htmlFor="activity-obfuscation-hint" style={{ ...s.label, marginBottom: 0 }}>Obfuscation Hint (optional)</label>
          <HelpTip label="Obfuscation hint">A teaser shown instead of the name while the activity is hidden, e.g. what to bring or wear. It appears on the timeline for everyone.</HelpTip>
        </div>
        <input id="activity-obfuscation-hint" type="text" value={form.hint} onChange={(e) => setForm({ ...form, hint: e.target.value })} placeholder='"Pack sunscreen!" or "Wear comfy shoes"' style={{ ...s.input, width: '100%' }} />
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: '4px 0 0' }}>
          Shown while obfuscated. Leave blank to show "Hidden activity".
        </p>
      </div>

      {/* Options row */}
      <div style={{ ...s.row, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <span style={s.option}>
            <label style={s.checkbox}>
              <input type="checkbox" checked={form.isCompetition} onChange={(e) => setForm({ ...form, isCompetition: e.target.checked, isTeamBased: e.target.checked && form.isTeamBased })} />
              🏆 Competition
            </label>
            <HelpTip label="Competition">Competition activities hold scores. They appear under Upcoming Competitions on the Scores page, and once concluded an admin assigns placements and points.</HelpTip>
          </span>
          {form.isCompetition && (
            <span style={s.option}>
              <label style={s.checkbox}>
                <input type="checkbox" checked={form.isTeamBased} onChange={(e) => setForm({ ...form, isTeamBased: e.target.checked })} />
                Team-based
              </label>
              <HelpTip label="Team-based">Scored per team instead of per player, using the teams from the Team Selection activity (or teams set up for this activity in Admin). In betting, every teammate bets from one shared team wallet.</HelpTip>
            </span>
          )}
          {!isEditing && (
            <span style={s.option}>
              <label style={s.checkbox}>
                <input type="checkbox" checked={form.publishImmediately} onChange={(e) => setForm({ ...form, publishImmediately: e.target.checked })} />
                Publish immediately
              </label>
              <HelpTip label="Publish immediately">Off saves a draft that only admins work on. On makes it live straight away. You can publish or unpublish later with 🚀 / 📝.</HelpTip>
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {onCancel && <button type="button" onClick={onCancel} style={s.cancel}>Cancel</button>}
          <button type="submit" disabled={!form.name.trim()} style={{ ...s.submit, opacity: form.name.trim() ? 1 : 0.5 }}>
            {isEditing ? (submitLabel || 'Save') : (form.publishImmediately ? 'Publish Now' : 'Save as Draft')}
          </button>
        </div>
      </div>
    </form>
  );
}
